import { create } from "zustand";

type AuthReturnState = {
  userId: string | null;
  authId: string | null;
};

// Last-resort fallback for environments not hosted by PHP (local, PR) where
// neither `window._msAuthUrl` nor `VITE_MS_AUTH_URL` is set.
const FALLBACK_AUTH_URL = "https://dev.app.mobsuccess.com/auth";

// Where PHP serves the auth app on its own hosts.
const PLATFORM_AUTH_PATH = "/auth";

// Per-tab breaker for the automatic validate-401 → auth app → authId →
// validate-401 cycle. The auth app hands back a fresh one-shot authId with no
// human step, so a backend that 401s `validate-auth` while still honoring
// `getauthid-auth` would otherwise loop forever. Reset by
// `resetAuthRedirectCount` once a session is validated.
const REDIRECT_COUNT_STORAGE_KEY = "ms-auth-redirect-count";
const MAX_AUTOMATIC_REDIRECTS = 3;

// Binds the `userId` / `authId` return leg to a hand-off this tab started. The
// pair is a bearer credential: exchanged without this check, a crafted link
// carrying somebody else's pair would sign the visitor into that account.
const RETURN_STATE_STORAGE_KEY = "ms-auth-return-state";
const RETURN_STATE_PARAM = "authState";

let isReturnSolicited = false;

export class AuthRedirectLoopError extends Error {
  constructor() {
    super("Too many automatic redirects to the auth app");
    this.name = "AuthRedirectLoopError";
  }
}

export function resetAuthRedirectCount(): void {
  writeRedirectCount(0);
}

// Resolves the auth app URL. `window._msAuthUrl`, when PHP injects it, always
// wins. A page served by PHP otherwise uses the auth app of its own host.
// Elsewhere (local, PR) an app can override via the build-time
// `VITE_MS_AUTH_URL`, falling back to the shared dev auth app.
//
// Resolved against the current origin: PHP may inject a path (`/auth`).
function getAuthAppUrl(): URL {
  return new URL(
    window._msAuthUrl ||
      (isServedByPlatform() ? PLATFORM_AUTH_PATH : "") ||
      import.meta.env.VITE_MS_AUTH_URL ||
      FALLBACK_AUTH_URL,
    window.location.origin,
  );
}

// PHP injects `window._msAuth` in every page it serves.
function isServedByPlatform(): boolean {
  return window._msAuth !== undefined;
}

// The www hosts (mobsuccess.com, its white labels) do not speak `to`: their
// auth page reads `url_logged`, a local path, signs the visitor in through the
// PHP session and hands the Cognito session back in `window._msAuth`. The app
// host is the one that names itself as the app REST origin.
function isServedByWwwPlatform(): boolean {
  return (
    isServedByPlatform() &&
    !window._msAuthUrl &&
    window._msApiEndpointApp !== window.location.origin
  );
}

function getWwwPlatformAuthUrl(): URL {
  const returnUrl = getCleanReturnUrl();
  const redirectUrl = new URL(PLATFORM_AUTH_PATH, window.location.origin);
  redirectUrl.searchParams.set(
    "url_logged",
    `${returnUrl.pathname}${returnUrl.search}`,
  );
  return redirectUrl;
}

// The `to` param is produced here but consumed by the PHP platform only: the
// auth app must never follow it client-side (see auth-index-redirect.ts — both
// producers of `to` target endpoints that reject a URL lacking a fresh
// userId/authId pair; only `url_logged` may be followed by the client).
//
// Throws `AuthRedirectLoopError` instead of redirecting once the per-tab
// breaker trips, so the caller surfaces an error state rather than looping.
export function redirectToAuth(): void {
  const count = readRedirectCount();
  if (count >= MAX_AUTOMATIC_REDIRECTS) {
    writeRedirectCount(0);
    throw new AuthRedirectLoopError();
  }
  writeRedirectCount(count + 1);
  window.location.href = getHandOffUrl().toString();
}

export function redirectToLogout(): void {
  const redirectUrl = getHandOffUrl();
  redirectUrl.searchParams.set("action", "sign-out");
  window.location.href = redirectUrl.toString();
}

function getHandOffUrl(): URL {
  if (isServedByWwwPlatform()) {
    return getWwwPlatformAuthUrl();
  }
  const redirectUrl = getAuthAppUrl();
  redirectUrl.searchParams.set("to", getReturnUrl(redirectUrl));
  return redirectUrl;
}

// Decide whether a page that just failed to obtain a Cognito session (the
// refresh token is expired or absent) should hand off to the auth app. False
// while an auth-app callback is still pending (userId/authId in the store) —
// redirecting then would loop between here and the auth app.
export function shouldRedirectToAuth(pending: AuthReturnState): boolean {
  return !pending.userId && !pending.authId;
}

export function getAuthReturnParams(): AuthReturnState {
  const queryParams = new URLSearchParams(window.location.search);
  return {
    userId: queryParams.get("userId"),
    authId: queryParams.get("authId"),
  };
}

export function cleanAuthReturnParams(): void {
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete("userId");
  cleanUrl.searchParams.delete("authId");
  cleanUrl.searchParams.delete(RETURN_STATE_PARAM);
  window.history.replaceState({}, "", cleanUrl.toString());
}

// Consume the auth-return params once they have been exchanged (successfully
// or not) for a Cognito session: strip them from the URL *and* reset the store.
// Unlike cleanAuthReturnParams (URL only), this clears userId/authId from the
// Zustand store, so shouldRedirectToAuth() stops treating the callback as
// pending — otherwise the stale values would linger for the rest of the
// page-load and a later refresh-token expiry would never hand off to the
// auth app.
export function consumeAuthReturnParams(): void {
  cleanAuthReturnParams();
  useAuthReturnParams.setState({ userId: null, authId: null });
}

// Captures the `userId` / `authId` params handed back by the auth app on
// return. The session layer reads these via `getState()` to retrieve the
// Cognito session from `getauthid-auth`.
export const useAuthReturnParams = create<AuthReturnState>((set) => {
  const { userId, authId } = getAuthReturnParams();
  const returnState = new URLSearchParams(window.location.search).get(
    RETURN_STATE_PARAM,
  );
  isReturnSolicited = takeReturnState(returnState);
  if (userId !== null || authId !== null || returnState !== null) {
    set({ userId, authId });
    cleanAuthReturnParams();
  }
  return { userId, authId };
});

// True when the `userId` / `authId` pair of this page-load came back with the
// state this tab sent along its last hand-off to the auth app.
export function isAuthReturnSolicited(): boolean {
  return isReturnSolicited;
}

// The URL the auth app sends the user back to: the current location minus the
// params of a previous auth round-trip. `action=sign-out` would sign the fresh
// session back out on return, and a stale `userId`/`authId` pair would shadow
// the fresh one.
//
// Relative when the auth app shares this origin: PHP's `to` whitelist accepts
// a path there, and rejects an absolute URL of its own host. The hash is left
// out: PHP appends `userId` / `authId` at the very end of a path, which would
// put them inside the fragment, where no app reads them.
function getReturnUrl(authAppUrl: URL): string {
  const returnUrl = getCleanReturnUrl();
  const returnState = getOrCreateReturnState();
  if (returnState !== null) {
    returnUrl.searchParams.set(RETURN_STATE_PARAM, returnState);
  }
  if (returnUrl.origin === authAppUrl.origin) {
    return `${returnUrl.pathname}${returnUrl.search}`;
  }
  return returnUrl.toString();
}

function getCleanReturnUrl(): URL {
  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.delete("action");
  returnUrl.searchParams.delete("userId");
  returnUrl.searchParams.delete("authId");
  returnUrl.searchParams.delete(RETURN_STATE_PARAM);
  return returnUrl;
}

// Reused until a return leg takes it: two hand-offs fired from the same
// document (StrictMode) must agree on the state the auth app sends back.
function getOrCreateReturnState(): string | null {
  try {
    const stored = window.sessionStorage.getItem(RETURN_STATE_STORAGE_KEY);
    if (stored) {
      return stored;
    }
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const returnState = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    window.sessionStorage.setItem(RETURN_STATE_STORAGE_KEY, returnState);
    return returnState;
  } catch {
    return null;
  }
}

// One-shot. Without sessionStorage there is nothing to compare against: the
// return leg is then trusted, as refusing it would loop on the auth app.
function takeReturnState(received: string | null): boolean {
  try {
    const expected = window.sessionStorage.getItem(RETURN_STATE_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_STATE_STORAGE_KEY);
    return expected !== null && received === expected;
  } catch {
    return true;
  }
}

function readRedirectCount(): number {
  try {
    return (
      Number(window.sessionStorage.getItem(REDIRECT_COUNT_STORAGE_KEY)) || 0
    );
  } catch {
    return 0;
  }
}

function writeRedirectCount(count: number): void {
  try {
    if (count === 0) {
      window.sessionStorage.removeItem(REDIRECT_COUNT_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(REDIRECT_COUNT_STORAGE_KEY, String(count));
    }
  } catch {
    // sessionStorage unavailable: no breaker.
  }
}
